import {
  getActivityMixFactors,
  getActivityMixFromStorey,
  getProgramActivityAreaFromActivityMix,
} from '@shared/helpers/design/activity-mix.helpers';
import { getCalculatedActivityAreaRecordFromStorey } from '@shared/helpers/design/activities.helpers';
import { getBuildingDesign } from './design.hooks';
import { Storey } from '@shared/models/design/storey.model';
import { ActivityMix } from '@shared/models/design/activities/activity-type.model';
import { useMemo } from 'react';
import { sumProperties } from '@shared/helpers/math.helpers';
import { useStoreyGfa } from './storeys.hooks';

/**
 * Get activity mix from storey. How much m2 of each activity type is in the storey.
 * @param storey Storey
 * @returns Activity mix (how much m2 of each activity type is in the storey)
 */
export const useActivityMixFromStorey = (storey: Storey) => {
  const design = getBuildingDesign();
  return useMemo(
    () => getActivityMixFromStorey(design, storey),
    [design, storey],
  );
};

/**
 * Get activity mix factors as factors (shares of 1) for each activity type.
 * @param storey Storey
 * @returns Activity mix factors, sum should be 1
 */
export const useActivityMixFactorsFromStorey = (storey: Storey) =>
  useActivityMixFactors(useActivityMixFromStorey(storey));

/**
 * Get activity mix factors as factors (shares of 1) for each activity type.
 * @param activityMix Activity mix
 * @returns Activity mix factors, sum should be 1
 */
const useActivityMixFactors = (activityMix: ActivityMix): ActivityMix =>
  useMemo(() => getActivityMixFactors(activityMix), [activityMix]);

export const useCalculatedActivityAreaRecordFromStorey = (storey: Storey) => {
  const design = getBuildingDesign();
  return useMemo(
    () => getCalculatedActivityAreaRecordFromStorey(design, storey),
    [design, storey],
  );
};

export const useCalculatedActivityAreaOnStorey = (storey: Storey) => {
  const record = useCalculatedActivityAreaRecordFromStorey(storey);
  return useMemo(() => sumProperties(record), [record]);
};

/**
 * Get the program activity area on a storey. This is the area that is not controlled by variables.
 * Same as NET INTERNAL AREA (NIA)
 * @param storey
 * @returns
 */
export const useProgramActivityAreaOnStorey = (storey: Storey) => {
  const activityMix = useActivityMixFromStorey(storey);
  return useMemo(
    () => getProgramActivityAreaFromActivityMix(activityMix, true), // (For now at least) include unset activity in as program area
    [activityMix],
  );
};

/**
 * Get the net internal area (NIA) on a storey. This is the area that is not controlled by variables.
 */
export const useStoreyNia = useProgramActivityAreaOnStorey;

/**
 * Get area that will be shared by activity references with share defined.
 * @param storey
 * @returns
 */
export const useSharedAreaOnStorey = (storey: Storey) => {
  const storeyGfa = useStoreyGfa(storey);
  const calculatedActivityArea = useCalculatedActivityAreaOnStorey(storey);
  return Math.max(0, storeyGfa - calculatedActivityArea);
};
